import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Sparkles, ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../hooks/useAnalytics';

/**
 * Lands the user when they click the magic-link email button.
 * Verifies the token, drops the JWT into AuthContext, and forwards to onboarding.
 */
const MagicLinkPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setError('No sign-in token in the URL.');
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/magic-verify?token=${encodeURIComponent(token)}`);
        applySession?.(data.token, data.user);
        trackEvent('signup_complete', { label: 'magic_link' });
        trackEvent('trial_started', { label: 'magic_link' });
        // Send them straight into the product
        navigate('/onboarding/business', { replace: true });
      } catch (err) {
        setError(err?.response?.data?.detail || 'This link is invalid or has expired.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#fdfaf6] flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-xl p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center shadow-md">
          <Sparkles className="w-6 h-6 text-white" strokeWidth={2.5} />
        </div>
        {!error ? (
          <>
            <h1 className="mt-5 text-2xl font-extrabold text-slate-900">Signing you in…</h1>
            <p className="mt-2 text-slate-600 text-sm">
              One sec — verifying your magic link.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-emerald-700 text-sm font-semibold">
              <ShieldCheck className="w-4 h-4" />
              7-day free trial · no card · cancel anytime
            </div>
          </>
        ) : (
          <>
            <div className="mt-5 w-10 h-10 mx-auto rounded-full bg-rose-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-rose-600" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Link expired</h1>
            <p className="mt-2 text-slate-600 text-sm">{error}</p>
            <Button
              data-testid="magic-link-retry"
              onClick={() => navigate('/signup')}
              className="mt-6 w-full h-11 bg-slate-900 text-white font-semibold rounded-lg"
            >
              Get a new sign-in link
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default MagicLinkPage;
